import { useQuery } from '@tanstack/react-query';
import { usersService } from '@/services/users.service';
import { SELECT_CLASS } from '../intake-form.constants';

interface EngineerSelectProps {
  value: string;
  onChange: (id: string) => void;
  disabled: boolean;
}

/** Reused verbatim from the original EquipmentIntakePage modal form — this is
 * the existing, correct initial-assignment mechanism for a brand-new intake
 * (a plain field set inside the intake+Inspection creation transaction), not
 * the Assignment Center's forceTransfer() (which is for reassigning an
 * already-existing inspection — see docs/OPS_EQUIPMENT_INTAKE_WORKSPACE_DESIGN.md §12). */
export function EngineerSelect({ value, onChange, disabled }: EngineerSelectProps) {
  const {
    data: users,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['tenant-users'],
    queryFn: () => usersService.listAll(),
    staleTime: 60_000,
  });

  if (isLoading) return <div className="h-10 animate-pulse rounded-lg bg-muted" />;
  if (isError) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-destructive">Failed to load engineers.</span>
        <button type="button" onClick={() => void refetch()} className="text-primary underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={SELECT_CLASS}
    >
      <option value="">— Unassigned —</option>
      {(users ?? []).map((u) => (
        <option key={u.id} value={u.id}>
          {u.firstName} {u.lastName}
          {u.username ? ` (${u.username})` : ''}
        </option>
      ))}
    </select>
  );
}
